/**
 * Profile Builder Utilities
 * 
 * Pure functions extracted from UserContext.tsx loadUserProfile.
 * These functions contain ZERO React code and make ZERO decisions about UX.
 * They are pure data transformations with deterministic outputs.
 * 
 * Per docs/invariants/PHASE_1_CORE.md:
 * - UserContext is the sole source of user state
 * - All profile data access flows through useUser()
 * 
 * EXTRACTION RULES (V2 Plan):
 * - Same inputs, same outputs as original inline code
 * - No new validation layers
 * - No new business logic
 */

// ============================================================================
// TYPES
// ============================================================================

export interface DailyTotal {
  day: string;
  minutes: number | null;
  sessions: number | null;
}

export interface FreezeLogEntry {
  frozen_date: string;
}

// ============================================================================
// PURE FUNCTIONS
// ============================================================================

/**
 * Calculate current streak from daily totals and freeze log.
 * 
 * Extracted from UserContext.tsx loadUserProfile (lines 386-413).
 * This is the EXACT same logic as the original.
 * 
 * @param dailyData - Array of daily totals from database
 * @param userTimezone - User's timezone string
 * @param freezeLog - Array of frozen dates from streak_freeze_log
 * @param getCurrentDate - Function to get current date in timezone
 * @param getRelativeDate - Function to get relative date in timezone
 * @returns Current streak count
 */
export function calculateCurrentStreak(
  dailyData: DailyTotal[] | null,
  userTimezone: string,
  freezeLog: FreezeLogEntry[] | null,
  getCurrentDate: (tz: string) => string,
  getRelativeDate: (days: number, tz: string) => string
): number {
  let currentStreak = 0;
  const streakToday = getCurrentDate(userTimezone);
  let currentDate = streakToday;
  
  const frozenDates = new Set(freezeLog?.map(f => f.frozen_date) || []);
  
  while (currentStreak < 365) {
    const dayData = dailyData?.find(d => d.day === currentDate);
    
    if (dayData && (dayData.minutes || 0) > 0) {
      currentStreak++;
    } else if (frozenDates.has(currentDate)) {
      // Day was frozen - continue streak
      currentStreak++;
    } else {
      if (currentDate !== streakToday) {
        break;
      }
      currentDate = getRelativeDate(-1, userTimezone);
      continue;
    }
    
    currentDate = getRelativeDate(-(currentStreak), userTimezone);
  }
  
  return currentStreak;
}

/**
 * Calculate longest streak from sorted daily totals.
 * 
 * Extracted from UserContext.tsx loadUserProfile (lines 438-461).
 * This is the EXACT same logic as the original.
 * 
 * @param dailyData - Array of daily totals from database
 * @param currentStreak - Current streak for comparison
 * @returns Longest streak count
 */
export function calculateLongestStreak(
  dailyData: DailyTotal[] | null,
  currentStreak: number = 0
): number {
  if (!dailyData || dailyData.length === 0) {
    return currentStreak;
  }
  
  let longestStreak = 0;
  let tempStreak = 0;
  
  // Sort by date ascending
  const sortedData = [...dailyData].sort(
    (a, b) => new Date(a.day).getTime() - new Date(b.day).getTime()
  );
  
  for (let i = 0; i < sortedData.length; i++) {
    if ((sortedData[i].minutes || 0) > 0) {
      tempStreak++;
      
      if (i < sortedData.length - 1) {
        const currentDay = new Date(sortedData[i].day);
        const nextDay = new Date(sortedData[i + 1].day);
        const dayDiff = (nextDay.getTime() - currentDay.getTime()) / (1000 * 60 * 60 * 24);
        
        if (dayDiff > 1) {
          longestStreak = Math.max(longestStreak, tempStreak);
          tempStreak = 0;
        }
      } else {
        longestStreak = Math.max(longestStreak, tempStreak);
      }
    } else {
      longestStreak = Math.max(longestStreak, tempStreak);
      tempStreak = 0;
    }
  }
  
  // Compare with current streak
  return Math.max(longestStreak, currentStreak);
}

/**
 * Calculate total minutes from daily totals.
 * 
 * Extracted from UserContext.tsx loadUserProfile (line 381).
 * 
 * @param dailyData - Array of daily totals
 * @returns Total minutes studied
 */
export function calculateTotalMinutes(dailyData: DailyTotal[] | null): number {
  return dailyData?.reduce((sum, day) => sum + (day.minutes || 0), 0) || 0;
}

/**
 * Calculate total sessions from daily totals.
 * 
 * Extracted from UserContext.tsx loadUserProfile (line 382).
 * 
 * @param dailyData - Array of daily totals
 * @returns Total number of sessions
 */
export function calculateTotalSessions(dailyData: DailyTotal[] | null): number {
  return dailyData?.reduce((sum, day) => sum + (day.sessions || 0), 0) || 0;
}

/**
 * Calculate weekly minutes from recent daily totals.
 * 
 * Extracted from UserContext.tsx loadUserProfile (line 475).
 * Accepts a simpler type since weekly query only selects minutes/sessions.
 * 
 * @param weeklyData - Array of daily totals for the past week (may not include day)
 * @returns Weekly minutes studied
 */
export function calculateWeeklyMinutes(weeklyData: { minutes: number | null }[] | null): number {
  return weeklyData?.reduce((sum, day) => sum + (day.minutes || 0), 0) || 0;
}
